import React, { useMemo } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import {
  formatDateTime,
  isBeforeByDate,
  isBetweenDates,
  TOTAL_DATE_CELLS,
} from "common/utils/dateUtil";
import { colors } from "common/const/colors";
import { CalendarCellWithPopover } from "./calendarCell";
import { isSameDay } from "date-fns/isSameDay";
import { I18N_KEYS } from "common/i18n/keys";

const Calendar = ({
  currentDate = new Date(),
  startDate,
  endDate = null,
  schedules,
  isRoundBorder = false,
  allowSelectOutside = false,
  onSelect,
  onPrevMonth,
  onNextMonth,
  disabledDate,
}) => {
  const { t } = useTranslation();

  const currentMonth = useMemo(() => currentDate.getMonth(), [currentDate]);
  const currentYear = useMemo(() => currentDate.getFullYear(), [currentDate]);

  const daysInMonth = useMemo(
    () => new Date(currentYear, currentMonth + 1, 0).getDate(),
    [currentYear, currentMonth],
  );

  const firstDayOfMonth = useMemo(
    () => new Date(currentYear, currentMonth, 1).getDay(),
    [currentYear, currentMonth],
  );

  const prevMonthLastDate = useMemo(
    () => new Date(currentYear, currentMonth, 0).getDate(),
    [currentYear, currentMonth],
  );

  const nextMonthDays = useMemo(() => {
    return TOTAL_DATE_CELLS - (firstDayOfMonth + daysInMonth);
  }, [firstDayOfMonth, daysInMonth]);

  const daysOfWeek = [
    t(I18N_KEYS.common.days.sun),
    t(I18N_KEYS.common.days.mon),
    t(I18N_KEYS.common.days.tue),
    t(I18N_KEYS.common.days.wed),
    t(I18N_KEYS.common.days.thu),
    t(I18N_KEYS.common.days.fri),
    t(I18N_KEYS.common.days.sat),
  ];

  const getScheduleStatusColor = (status) => {
    switch (status) {
      case 0:
        return colors.White;
      case 1:
        return colors.Gray;
      case 2:
        return colors.LightOrange;
      case 3:
        return colors.LightGreen;
      case 4:
        return colors.LightYellow;
      case 5:
        return colors.LightRed;
      default:
        return "transparent";
    }
  };

  const getBorderRadius = (cellDate, dateA, dateB) => {
    let result = ["0", "0", "0", "0"];

    const startScheduleDate = schedules?.[formatDateTime(cellDate)]?.start;
    const endScheduleDate = schedules?.[formatDateTime(cellDate)]?.end;

    if (isSameDay(cellDate, startScheduleDate)) {
      result[0] = "50px";
      result[3] = "50px";
    }
    if (isSameDay(cellDate, endScheduleDate)) {
      result[1] = "50px";
      result[2] = "50px";
    }

    if (dateA && dateB) {
      const start = dateA < dateB ? dateA : dateB;
      const end = dateA < dateB ? dateB : dateA;

      if (isBetweenDates(cellDate, dateA, dateB)) result = ["0", "0", "0", "0"];
      if (isSameDay(cellDate, start)) {
        result[0] = "50px";
        result[3] = "50px";
      }
      if (isSameDay(cellDate, end)) {
        result[1] = "50px";
        result[2] = "50px";
      }
    } else if (dateA && !dateB && isSameDay(cellDate, dateA)) {
      result = ["50px", "50px", "50px", "50px"];
    }

    return result.join(" ");
  };

  const getCellStyle = (
    isRoundBorder,
    cellDate,
    selectedDate, // startDate || endDate
    anotherDate, // endDate
    status,
    color = colors.Gray,
  ) => {
    // 1. 날짜 비교 시 getTime() 대신 이 프로젝트에서 이미 사용 중인 isSameDay 사용
    const isSelected = selectedDate && isSameDay(cellDate, selectedDate);
    const isSelectedAnother = anotherDate && isSameDay(cellDate, anotherDate);
    const isInRange =
      selectedDate &&
      anotherDate &&
      isBetweenDates(cellDate, selectedDate, anotherDate);

    return {
      // 텍스트 색상 결정
      color: color === colors.Gray && status === 1 ? colors.White : color,

      // 배경색 결정 (선택되었거나 범위 내에 있을 때 LightBlue)
      backgroundColor:
        isSelected || isSelectedAnother || isInRange
          ? colors.LightBlue
          : getScheduleStatusColor(status),

      // 보더 라운드 결정
      borderRadius: isRoundBorder
        ? getBorderRadius(cellDate, selectedDate, anotherDate)
        : null,
    };
  };

  return (
    <CalendarContainer>
      <CalendarWrapper>
        <CalendarTable>
          {daysOfWeek.map((day, index) => (
            <DayHeader key={index}>{day}</DayHeader>
          ))}

          {/* 이전 달 */}
          {Array.from({ length: firstDayOfMonth }).map((_, i) => {
            const day = prevMonthLastDate - firstDayOfMonth + i + 1;
            const cellDate = new Date(currentYear, currentMonth - 1, day);
            return (
              <CalendarCellWithPopover
                key={`prev-${day}`}
                date={cellDate}
                style={getCellStyle(
                  isRoundBorder,
                  cellDate,
                  startDate || endDate,
                  endDate,
                  schedules?.[formatDateTime(cellDate)]?.status,
                )}
                scheduleDetail={{
                  status: schedules?.[formatDateTime(cellDate)]?.status,
                  details: schedules?.[formatDateTime(cellDate)]?.details,
                }}
                onSelect={() => {
                  if (!allowSelectOutside) return;
                  onPrevMonth?.();
                  onSelect?.(cellDate);
                }}
                allowSelect={allowSelectOutside}>
                {day}
              </CalendarCellWithPopover>
            );
          })}

          {/* 현재 달 */}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1;
            const cellDate = new Date(currentYear, currentMonth, day);
            const disabled = disabledDate?.(cellDate);
            return (
              <CalendarCellWithPopover
                key={`day-${day}`}
                date={cellDate}
                style={getCellStyle(
                  isRoundBorder,
                  cellDate,
                  startDate || endDate,
                  endDate,
                  schedules?.[formatDateTime(cellDate)]?.status,
                  disabled ? colors.Gray : colors.DarkNavy,
                )}
                scheduleDetail={{
                  status: schedules?.[formatDateTime(cellDate)]?.status,
                  details: schedules?.[formatDateTime(cellDate)]?.details,
                }}
                allowSelect={!disabled}
                onSelect={() => {
                  console.log("selected: ", cellDate);
                  if (disabled) return;
                  onSelect?.(cellDate);
                }}
              />
            );
          })}

          {/* 다음 달 */}
          {Array.from({ length: nextMonthDays }).map((_, i) => {
            const day = i + 1;
            const cellDate = new Date(currentYear, currentMonth + 1, day);
            return (
              <CalendarCellWithPopover
                key={`next-${day}`}
                date={cellDate}
                style={getCellStyle(
                  isRoundBorder,
                  cellDate,
                  startDate || endDate,
                  endDate,
                  schedules?.[formatDateTime(cellDate)]?.status,
                )}
                scheduleDetail={{
                  status: schedules?.[formatDateTime(cellDate)]?.status,
                  details: schedules?.[formatDateTime(cellDate)]?.details,
                }}
                onSelect={() => {
                  if (!allowSelectOutside) return;
                  onNextMonth?.();
                  onSelect?.(cellDate);
                }}
                allowSelect={allowSelectOutside}>
                {day}
              </CalendarCellWithPopover>
            );
          })}
        </CalendarTable>
      </CalendarWrapper>
    </CalendarContainer>
  );
};

const CalendarContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  box-sizing: border-box;
`;

const CalendarWrapper = styled.div`
  width: 100%;
  height: 100%;
  padding: 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid var(--Gray);
  box-sizing: border-box;
`;

const CalendarTable = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-template-rows: repeat(7, minmax(2.25rem, 1fr));
  gap: 0.5rem 0;
  width: 100%;
  min-width: 24rem;
  height: 100%;
  box-sizing: border-box;
`;

const DayHeader = styled.div`
  min-height: 2.25rem;
  text-align: center;
  color: ${({ color }) => color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: var(--font-weight-bold);
  box-sizing: border-box;

  &:nth-child(7n + 1) {
    color: var(--Red);
  } // 일요일
  &:nth-child(7n) {
    color: var(--Blue);
  } // 토요일
  &:not(:nth-child(7n + 1)):not(:nth-child(7n)) {
    color: var(--DarkNavy);
  } // 평일
`;

export { Calendar };
